import { createContext, useContext } from "react";
import type { Session, User } from "@supabase/supabase-js";
import type { ModuleConfig, ModuleKey } from "@/lib/modules";

export type AppRole = "admin" | "assessor" | "approver" | "expert" | "vendor";

export type Profile = {
  user_type: string;
  sector: string | null;
  tier: string;
  full_name: string | null;
  organisation: string | null;
  city: string | null;
  borough: string | null;
  ward: string | null;
  area_locality: string | null;
  organisation_id: string | null;
};

export type Organisation = {
  id: string;
  name: string;
  subscription_tier: string;
  module_config: ModuleConfig | null;
  assessment_access_expires_at: string | null;
  assessment_access_source: string | null;
};

export type AssessmentAccess = {
  enabled: boolean;
  expiresAt: string | null;
  expired: boolean;
  isDemo: boolean;
};

export interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: AppRole | null;
  roles: AppRole[];
  activeRole: AppRole | null;
  setActiveRole: (role: AppRole) => void;
  profile: Profile | null;
  organisation: Organisation | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  isModuleEnabled: (key: ModuleKey) => boolean;
  assessmentAccess: AssessmentAccess;
  refreshProfile: () => Promise<void>;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
